import {
	BigNum,
	isVariant,
	OrderAction,
	OrderActionExplanation,
	OrderType,
	PositionDirection,
} from '@drift-labs/sdk';
import { ENUM_UTILS, MarketId, matchEnum } from '@drift/common';
import XButton from 'src/components/Buttons/XButton';
import Utility from 'src/components/Inputs/Utility';
import Text from 'src/components/Text/Text';
import { AuctionState } from 'src/providers/auctionDataProvider';
import { SlotContext } from 'src/providers/currentSlotProvider';
import { useContext, useEffect, useRef, useState } from 'react';
import {
	buildStyles,
	CircularProgressbar,
	CircularProgressbarWithChildren,
} from 'react-circular-progressbar';
import Toast from './Toast';
import useCurrentSettings from '../../hooks/useCurrentSettings';
import usePriceForOracleOffsetOrder from 'src/hooks/usePriceForOracleOffsetOrders';

const AuctionAlert = ({
	auction,
	onClose,
}: {
	auction: AuctionState;
	onClose: () => void;
}) => {
	// # Constants
	const currentSlot = useContext(SlotContext).currentSlot;

	const oraclePrice = usePriceForOracleOffsetOrder(
		new MarketId(auction.market.marketIndex, auction.order.marketType)
	);

	const [{ showFillNotifications }] = useCurrentSettings();

	const auctionIsFinished = currentSlot - auction.startSlot > auction.duration;
	const orderIsFilled = auction.baseAmountFilled === auction.baseAmount;
	const orderIsPartiallyFilled = auction.baseAmountFilled > 0;

	let priceImprovement = 0;
	let fillPrice = 0;
	let priceWasImproved = false;

	if (auction.baseAmountFilled !== 0) {
		fillPrice = auction.quoteAmountFilled / auction.baseAmountFilled;
	}

	if (orderIsFilled) {
		const priceDiffVamm = Math.abs(
			auction.auctionStartPrice - auction.auctionEndPrice
		);
		const priceDiffFill = Math.abs(auction.auctionStartPrice - fillPrice);

		priceWasImproved = priceDiffFill < priceDiffVamm;

		if (priceWasImproved) {
			priceImprovement =
				(Math.abs(auction.auctionEndPrice - fillPrice) /
					auction.auctionEndPrice) *
				100;
		}
	}

	// # State
	const [successState, setSuccessState] = useState<
		'' | 'success' | 'slippageLimitReached'
	>();

	const fillHandled = useRef(false);

	// # State-Dependent constants
	// # Methods

	// # Effect Hooks
	useEffect(() => {
		if (orderIsFilled && !fillHandled.current) {
			fillHandled.current = true;
			setTimeout(() => {
				setSuccessState('success');
			}, 400);
		}
	}, [orderIsFilled]);

	useEffect(() => {
		if (matchEnum(auction.lastAction, OrderAction.CANCEL)) {
			if (
				matchEnum(
					auction.actionExplanation,
					OrderActionExplanation.MARKET_ORDER_FILLED_TO_LIMIT_PRICE
				)
			) {
				setSuccessState('slippageLimitReached');
			}
		}
	}, [ENUM_UTILS.toStr(auction.lastAction)]);

	const isLong = matchEnum(auction.order.direction, PositionDirection.LONG);

	const worstPrice =
		auction.order.oraclePriceOffset ||
		ENUM_UTILS.match(auction.order.orderType, OrderType.ORACLE)
			? oraclePrice.add(auction.order.auctionEndPrice)
			: auction.order.price;

	// # Rendering
	return (
		showFillNotifications && (
			<Toast type={orderIsFilled ? 'success' : null}>
				<div className="flex items-start justify-between w-full h-full">
					<div className="flex">
						<div className="w-[30px] h-[30px] mt-0.5 mr-4 shrink-0">
							{orderIsFilled && successState === 'success' ? (
								<>
									<div className="flex items-center justify-center w-full h-full">
										<Toast.SuccessIcon />
									</div>
								</>
							) : successState === 'slippageLimitReached' ? (
								<>
									<div className="flex items-center justify-center w-full h-full">
										<Toast.ErrorIcon />
									</div>
								</>
							) : (
								<>
									<CircularProgressbarWithChildren
										value={
											((currentSlot - auction.startSlot) / auction.duration) *
											100
										}
										strokeWidth={6}
										styles={buildStyles({
											pathColor: 'var(--darkBlue-30)',
											trailColor: 'transparent',
											pathTransitionDuration: 0.15,
										})}
									>
										<div style={{ width: '72%' }}>
											<CircularProgressbar
												// value={50}
												value={
													(auction.baseAmountFilled / auction.baseAmount) * 100
												}
												strokeWidth={50}
												styles={buildStyles({
													pathColor: 'var(--darkBlue-20)',
													trailColor: 'transparent',
													pathTransitionDuration: 0.15,
												})}
											/>
										</div>
									</CircularProgressbarWithChildren>
								</>
							)}
						</div>
						<div className="flex flex-col">
							<Text.H2 className="text-text-emphasis">
								{successState === 'slippageLimitReached'
									? 'Slippage Limit Reached'
									: priceWasImproved
									? `Order Filled`
									: orderIsFilled
									? 'Order Filled'
									: auctionIsFinished
									? 'Completing your order'
									: 'Finding the best price'}
							</Text.H2>

							{priceWasImproved ? (
								<>
									<Utility.VERTSPACERS />
									<Text.BODY1 className="text-text-default">
										{`Your fill price was ${BigNum.from(
											priceImprovement * 10 ** 6,
											6
										).printShort(false, 3)}% better than the vAMM price`}
									</Text.BODY1>
								</>
							) : (
								<></>
							)}

							{orderIsFilled ? (
								<>
									<Utility.VERTSPACERS />
									<Text.BODY2 className="text-text-secondary">
										{isVariant(auction.order.marketType, 'perp')
											? isLong
												? 'Long'
												: 'Short'
											: isLong
											? 'Buy'
											: 'Sell'}{' '}
										{auction.baseAmount} {auction.market.symbol} with an average
										fill price{' '}
										{BigNum.from(fillPrice * 10 ** 6, 6).toNotional(true)}
									</Text.BODY2>
								</>
							) : successState === 'slippageLimitReached' ? (
								<>
									<Utility.VERTSPACERS />
									<Text.BODY2 className="text-text-secondary">
										Your order was{' '}
										{orderIsPartiallyFilled ? 'Partially Filled' : 'Cancelled'}{' '}
										due to price exceeding your slippage tolerance
									</Text.BODY2>
									{orderIsPartiallyFilled ? (
										<>
											<Utility.VERTSPACERS />
											<Text.BODY1 className="text-text-default">
												Filled: {auction.baseAmountFilled} /{' '}
												{auction.baseAmount} {auction.market.symbol} with an
												average fill price of{' '}
												{BigNum.from(fillPrice * 10 ** 6, 6).toNotional(true)}
											</Text.BODY1>
										</>
									) : (
										<></>
									)}
								</>
							) : (
								<>
									<Utility.VERTSPACERXS />
									<Text.BODY1 className="text-text-default">
										Filled: {auction.baseAmountFilled} / {auction.baseAmount}{' '}
										{auction.market.symbol}
									</Text.BODY1>
									<Utility.VERTSPACERS />
									<div className="flex space-x-2 text-text-secondary">
										<Text.BODY2 className="w-[100px]">
											Avg Fill Price
										</Text.BODY2>
										<Text.BODY2>
											{/* {BigNum.from(
											auction.currentPriceEstimate * 10 ** 6,
											6
										).toNotional(true)} */}
											{fillPrice
												? BigNum.from(fillPrice * 10 ** 6, 6).toNotional(true)
												: '-'}
										</Text.BODY2>
									</div>
									{/* {fillPrice ? (
									<>
										<Utility.VERTSPACERS />
										<div className="flex space-x-2 text-text-secondary">
											<Text.BODY2 className="w-[100px]">
												Avg Fill Price
											</Text.BODY2>
											<Text.BODY2>
												{BigNum.from(fillPrice * 10 ** 6, 6).toNotional(true)}
											</Text.BODY2>
										</div>
									</>
								) : (
									<></>
								)} */}
									{worstPrice ? (
										<>
											<Utility.VERTSPACERXS />
											<div className="flex space-x-2 text-text-secondary">
												<Text.BODY2 className="w-[100px]">
													{`${isLong ? 'Max' : 'Min'} Fill Price`}
												</Text.BODY2>
												<Text.BODY2>{worstPrice.toNotional(true)}</Text.BODY2>
											</div>
										</>
									) : (
										<></>
									)}
								</>
							)}
						</div>
					</div>
					<div>
						<XButton
							stroke="var(--neutrals-40)"
							onClick={(e) => {
								e.stopPropagation();
								onClose();
							}}
						/>
					</div>
				</div>
			</Toast>
		)
	);
};

export default AuctionAlert;
